import React, { useState, useEffect } from "react";
import { Box, Card, Typography, Avatar, Button, Grid, useMediaQuery, Container, Fade, Grow } from "@mui/material";
import { AccountCircle, Star, School, Assessment, SupportAgent } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const ExamPrepSection = ({ testSeries }) => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery("(max-width:768px)");
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 300);
        return () => clearTimeout(timer);
    }, []);

    const features = [
        {
            id: 1,
            title: "Expert Mentorship",
            description:
                "We have helped more than 5000 students clear their CA exams. CA Apurva Deshpande brings years of experience, insight, and dedication to ensure you receive the best guidance.",
            icon: <AccountCircle style={{ fontSize: 40 }} />,
            borderColor: "#E91E63",
            gradient: "linear-gradient(135deg, #E91E63 0%, #C2185B 100%)",
            position: { top: "5%", left: "12%" },
            lineCoords: { x1: "50%", y1: "50%", x2: "20%", y2: "20%" },
        },
        {
            id: 2,
            title: "Proven Track Record",
            description:
                "Our students' success speaks for itself. We have produced 15+ All-India Rankers and countless others who have passed with flying colors.",
            icon: <Star style={{ fontSize: 40 }} />,
            borderColor: "#2196F3",
            gradient: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
            position: { top: "-10%", right: "41%" },
            lineCoords: { x1: "50%", y1: "50%", x2: "50%", y2: "8%" },
        },
        {
            id: 3,
            title: "Structured Learning Approach",
            description:
                "Our tests are not just assessments but also an opportunity to identify weak areas, improve time management, and enhance exam strategies.",
            icon: <School style={{ fontSize: 40 }} />,
            borderColor: "#FF9800",
            gradient: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
            position: { bottom: "55%", right: "12%" },
            lineCoords: { x1: "50%", y1: "50%", x2: "80%", y2: "20%" },
        },
        {
            id: 4,
            title: "Dedicated Support",
            description:
                "Get personalized feedback, study strategies, and expert advice from our experienced mentors. Your success is our priority.",
            icon: <SupportAgent style={{ fontSize: 40 }} />,
            borderColor: "#9C27B0",
            gradient: "linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)",
            position: { bottom: "15%", left: "8%" },
            lineCoords: { x1: "50%", y1: "50%", x2: "20%", y2: "80%" },
        },
        {
            id: 5,
            title: "Comprehensive Test Series",
            description:
                "We offer portion-wise and full-course test series designed to cover all topics, allowing you to build your skills step-by-step.",
            icon: <Assessment style={{ fontSize: 40 }} />,
            borderColor: "#4CAF50",
            gradient: "linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)",
            position: { bottom: "15%", right: "8%" },
            lineCoords: { x1: "50%", y1: "50%", x2: "80%", y2: "80%" },
        },
    ];

    const handleScroll = () => {
        navigate('/')
        testSeries.current?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <Box
            component="section"
            sx={{
                position: 'relative',
                // py: { xs: 6, md: 8 },
                // px: { xs: 2, md: 4 },
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at 20% 80%, rgba(233, 30, 99, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(33, 150, 243, 0.03) 0%, transparent 50%)',
                    pointerEvents: 'none',
                }
            }}
        >
            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Header Section */}
                <Fade in={isVisible} timeout={800}>
                    <Grid container sx={{ display: "flex", justifyContent: "center", textAlign: 'center', mb: 4 }}>
                        <Grid item xs={12} sm={8} md={8} lg={8}>
                            <Typography 
                                sx={{ 
                                    mb: 3, 
                                    mt: 4,
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #114CAF 0%, #1976D2 100%)',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    fontSize: { xs: '14px', md: '20px' },
                                    position: 'relative',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: -8,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 60,
                                        height: 3,
                                        background: 'linear-gradient(135deg, #114CAF 0%, #1976D2 100%)',
                                        borderRadius: 2,
                                    }
                                }}
                            >
                                Vsmart Examprep - Your Path to Success in CA Exams
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'text.secondary',
                                    fontSize: isMobile ? '14px' : '16px',
                                    lineHeight: 1.7,
                                    maxWidth: 700,
                                    mx: 'auto',
                                    mb: isMobile ? 0 : 6
                                }}
                            >
                                We are committed to helping you achieve your dreams of becoming a Chartered Accountant.
                                Since 2018, we have been empowering CA aspirants across India with our expertly designed test series.
                                Whether you're preparing for the CA Foundation, Intermediate, or Final exams,
                                we provide comprehensive portion-wise and full-course test series at all levels,
                                tailored to maximize your preparation and performance.
                            </Typography>
                        </Grid>
                    </Grid>
                </Fade>

                {/* Mobile Grid Layout */}
                <Box sx={{ display: isMobile ? "block" : "none", padding: "15px" }}>
                    <Grow in={isVisible} timeout={1000}>
                        <Grid container spacing={3}>
                            {features.map((feature, i) => (
                                <Grid item xs={6} sm={6} md={6} lg={6} key={feature.id} sx={{paddingLeft: "0px !important"}}>
                                    <Card
                                        sx={{
                                            margin: '8px',
                                            padding: "20px",
                                            textAlign: "left",
                                            borderRadius: "16px",
                                            border: `2px solid ${feature.borderColor}`,
                                            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                                            background: 'rgba(255, 255, 255, 0.95)',
                                            backdropFilter: 'blur(10px)',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            minHeight: '200px',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                height: 4,
                                                background: feature.gradient,
                                            },
                                            '&:hover': {
                                                transform: 'translateY(-8px) scale(1.02)',
                                                boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                                                '& .feature-icon': {
                                                    transform: 'scale(1.1) rotate(5deg)',
                                                },
                                                '& .feature-number': {
                                                    transform: 'scale(1.1)',
                                                }
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 2, justifyContent: "space-between" }}>
                                            <Box
                                                className="feature-icon"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 50,
                                                    height: 50,
                                                    borderRadius: '50%',
                                                    background: feature.gradient,
                                                    color: '#fff',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                                }}
                                            >
                                                {React.cloneElement(feature.icon, { 
                                                    sx: { fontSize: 24, color: '#fff' } 
                                                })}
                                            </Box>
                                            <Typography 
                                                className="feature-number"
                                                variant="subtitle1" 
                                                fontWeight="bold" 
                                                sx={{ 
                                                    padding: "8px 12px", 
                                                    borderRadius: "50%", 
                                                    color: feature.borderColor, 
                                                    border: `2px solid ${feature.borderColor}`,
                                                    transition: 'all 0.3s ease',
                                                    fontSize: '0.9rem',
                                                    minWidth: 35,
                                                    textAlign: 'center'
                                                }}
                                            >
                                                {i + 1}
                                            </Typography>
                                        </Box>
                                        <Typography 
                                            variant="subtitle1" 
                                            fontWeight="bold" 
                                            mb={1} 
                                            sx={{ 
                                                color: feature.borderColor,
                                                fontSize: '14px'
                                            }}
                                        >
                                            {feature.title}
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                color: 'text.secondary',
                                                lineHeight: 1.6,
                                                fontSize: '12px'
                                            }}
                                        >
                                            {feature.description}
                                        </Typography>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Grow>
                    <Fade in={isVisible} timeout={1200}>
                        <Box sx={{ mb: 3, textAlign: "center", mt: 4 }}>
                            <Button 
                                onClick={handleScroll}
                                sx={{ 
                                    background: "linear-gradient(135deg, #114CAF 0%, #1976D2 100%)", 
                                    padding: "12px 48px", 
                                    color: "#fff", 
                                    fontWeight: "600", 
                                    fontSize: "14px",
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    boxShadow: '0 8px 24px rgba(17, 76, 175, 0.3)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #0d47a1 0%, #1565C0 100%)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 32px rgba(17, 76, 175, 0.4)',
                                    }
                                }}
                            >
                                Explore Now
                            </Button>
                        </Box>
                    </Fade>
                </Box>

                {/* Desktop Layout with SVG and Positioned Cards */}
                <Box sx={{ 
                    position: "relative", 
                    width: "90%", 
                    margin: "auto", 
                    textAlign: "center", 
                    pt: 8, 
                    height: "600px", 
                    mt: 6, 
                    display: isMobile ? "none" : "block"
                }}>
                    <svg height="100%" width="100%" style={{ position: "absolute", top: 0, left: 0, zIndex: -1 }}>
                        {features.map((feature, index) => (
                            <line
                                key={index}
                                x1={feature.lineCoords.x1}
                                y1={feature.lineCoords.y1}
                                x2={feature.lineCoords.x2}
                                y2={feature.lineCoords.y2}
                                stroke={feature.borderColor}
                                strokeWidth="2"
                                strokeDasharray="5,5"
                                opacity="0.7"
                            />
                        ))}
                    </svg>

                    {/* Student Avatar */}
                    <Fade in={isVisible} timeout={800}>
                        <Box sx={{ 
                            position: "absolute", 
                            top: "50%", 
                            left: "50%", 
                            transform: "translate(-50%, -50%)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            zIndex: 5
                        }}>
                            <Avatar
                                src="img/students-circle.png"
                                sx={{ 
                                    width: 120, 
                                    height: 120,
                                    border: '4px solid #fff',
                                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'scale(1.05)',
                                        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2)',
                                    }
                                }}
                            />
                            <Typography variant="h6" fontWeight="600" mt={1} sx={{ color: 'text.primary' }}>
                                Future CA
                            </Typography>
                        </Box>
                    </Fade>

                    {/* Feature Cards */}
                    {features.map((feature, i) => (
                        <Grow key={feature.id} in={isVisible} timeout={1000 + (i * 200)}>
                            <Card
                                sx={{
                                    position: "absolute",
                                    width: "240px",
                                    padding: "16px",
                                    textAlign: "left",
                                    borderRadius: "16px",
                                    border: `2px solid ${feature.borderColor}`,
                                    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                                    background: 'rgba(255, 255, 255, 0.95)',
                                    backdropFilter: 'blur(10px)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                    ...feature.position,
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: 3,
                                        background: feature.gradient,
                                    },
                                    '&:hover': {
                                        transform: 'translateY(-4px) scale(1.02)',
                                        boxShadow: '0 16px 40px rgba(0,0,0,0.15)',
                                        zIndex: 10,
                                    }
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5, justifyContent: "space-between" }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 45,
                                            height: 45,
                                            borderRadius: '50%',
                                            background: feature.gradient,
                                            color: '#fff',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                        }}
                                    >
                                        {React.cloneElement(feature.icon, { 
                                            sx: { fontSize: 22, color: '#fff' } 
                                        })}
                                    </Box>
                                    <Typography 
                                        variant="subtitle1" 
                                        fontWeight="bold" 
                                        sx={{ 
                                            padding: "6px 10px", 
                                            borderRadius: "50%", 
                                            color: feature.borderColor, 
                                            border: `2px solid ${feature.borderColor}`,
                                            fontSize: '0.85rem',
                                            minWidth: 30,
                                            textAlign: 'center'
                                        }}
                                    >
                                        {i + 1}
                                    </Typography>
                                </Box>
                                <Typography 
                                    variant="subtitle1" 
                                    fontWeight="bold" 
                                    mb={1} 
                                    sx={{ 
                                        color: feature.borderColor,
                                        fontSize: '14px'
                                    }}
                                >
                                    {feature.title}
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: 'text.secondary',
                                        fontSize: '12px',
                                        lineHeight: 1.5
                                    }}
                                >
                                    {feature.description}
                                </Typography>
                            </Card>
                        </Grow>
                    ))}

                    <Fade in={isVisible} timeout={1400}>
                        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, mb: 5, mt: 1 }}>
                            <Button 
                                onClick={handleScroll}
                                sx={{ 
                                    background: "linear-gradient(135deg, #114CAF 0%, #1976D2 100%)", 
                                    padding: "10px 35px", 
                                    color: "#fff", 
                                    fontWeight: "600", 
                                    fontSize: "14px",
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    boxShadow: '0 8px 24px rgba(17, 76, 175, 0.3)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #0d47a1 0%, #1565C0 100%)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 32px rgba(17, 76, 175, 0.4)',
                                    }
                                }}
                            >
                                Explore Now
                            </Button>
                        </Box>
                    </Fade>
                </Box>

                {/* Floating decorative elements */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '15%',
                        right: '5%',
                        width: { xs: 60, md: 80 },
                        height: { xs: 60, md: 80 },
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.08) 0%, rgba(194, 24, 91, 0.04) 100%)',
                        filter: 'blur(20px)',
                        animation: 'float 6s ease-in-out infinite',
                        '@keyframes float': {
                            '0%, 100%': { transform: 'translateY(0px)' },
                            '50%': { transform: 'translateY(-20px)' },
                        },
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '15%',
                        left: '5%',
                        width: { xs: 40, md: 60 },
                        height: { xs: 40, md: 60 },
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(25, 118, 210, 0.04) 100%)',
                        filter: 'blur(15px)',
                        animation: 'float 8s ease-in-out infinite reverse',
                    }}
                />
            </Container>
        </Box>
    );
};

export default ExamPrepSection;
